import { CompanyType } from '../../../companyService/company/types/enums';
import { UserRole } from '../../roles/types/enums';

/**
 * Which roles a caller of a given role may ASSIGN, per company type, when creating or updating a
 * user. This is the single source the auth-service guard validates against (its `determineRole`)
 * and that clients read to offer only valid role choices — so the role picker and the server guard
 * never disagree.
 *
 * A caller role absent from a company type's map may assign nothing. Location-bounded limits (a
 * Location Manager only within its own branches) are enforced by the use-case, not expressed here.
 */
export const determineRoleConfig: { [key in CompanyType]: { [callerRole: string]: UserRole[] } } = {
  [CompanyType.SELF_INSPECTION]: {
    [UserRole.SUPER_ADMIN]: [
      UserRole.INSPECTOR,
      UserRole.PROVEN_INSPECTOR,
      UserRole.TRAINEE_INSPECTOR,
      UserRole.AUDITOR,
    ],
  },
  [CompanyType.DEALER]: {
    [UserRole.SUPER_ADMIN]: [
      UserRole.COMPANY_ROOT,
      UserRole.COMPANY_ADMIN,
      UserRole.COMPANY_STANDARD_USER,
      UserRole.COMPANY_ADVANCED_USER,
      UserRole.COMPANY_LOCATION_MANAGER,
      UserRole.COMPANY_CUSTOMS_USER,
    ],
    [UserRole.COMPANY_ROOT]: [
      UserRole.COMPANY_ADMIN,
      UserRole.COMPANY_STANDARD_USER,
      UserRole.COMPANY_ADVANCED_USER,
      UserRole.COMPANY_LOCATION_MANAGER,
    ],
    // Self-replicating by design: the story requires a Company Admin to create other Company Admins.
    [UserRole.COMPANY_ADMIN]: [
      UserRole.COMPANY_ADMIN,
      UserRole.COMPANY_STANDARD_USER,
      UserRole.COMPANY_ADVANCED_USER,
      UserRole.COMPANY_LOCATION_MANAGER,
    ],
    // A Location Manager may assign only roles BELOW its own rank — not another Location Manager.
    // Grants are further bounded to the locations it holds, enforced by the use-case.
    [UserRole.COMPANY_LOCATION_MANAGER]: [UserRole.COMPANY_STANDARD_USER, UserRole.COMPANY_ADVANCED_USER],
  },
  [CompanyType.LEASING]: {
    [UserRole.SUPER_ADMIN]: [
      UserRole.COMPANY_ROOT,
      UserRole.COMPANY_ADMIN,
      UserRole.COMPANY_STANDARD_USER,
      UserRole.COMPANY_ADVANCED_USER,
      UserRole.COMPANY_LOCATION_MANAGER,
      UserRole.COMPANY_READ_ONLY_USER,
      UserRole.COMPANY_INSPECTOR_COORDINATOR,
      UserRole.COMPANY_CUSTOMS_USER,
    ],
    [UserRole.COMPANY_ROOT]: [
      UserRole.COMPANY_ADMIN,
      UserRole.COMPANY_STANDARD_USER,
      UserRole.COMPANY_ADVANCED_USER,
      UserRole.COMPANY_LOCATION_MANAGER,
      UserRole.COMPANY_READ_ONLY_USER,
      UserRole.COMPANY_INSPECTOR_COORDINATOR,
    ],
    [UserRole.COMPANY_ADMIN]: [
      UserRole.COMPANY_ADMIN,
      UserRole.COMPANY_STANDARD_USER,
      UserRole.COMPANY_ADVANCED_USER,
      UserRole.COMPANY_LOCATION_MANAGER,
      UserRole.COMPANY_READ_ONLY_USER,
      UserRole.COMPANY_INSPECTOR_COORDINATOR,
    ],
    // Only roles below Location Manager — not another Location Manager, and not Inspector
    // Coordinator (which outranks it). Bounded to its held locations by the use-case.
    [UserRole.COMPANY_LOCATION_MANAGER]: [
      UserRole.COMPANY_STANDARD_USER,
      UserRole.COMPANY_ADVANCED_USER,
      UserRole.COMPANY_READ_ONLY_USER,
    ],
  },
  [CompanyType.RENTAL]: {
    [UserRole.SUPER_ADMIN]: [
      UserRole.COMPANY_ROOT,
      UserRole.COMPANY_ADMIN,
      UserRole.COMPANY_STANDARD_USER,
      UserRole.COMPANY_ADVANCED_USER,
      UserRole.COMPANY_LOCATION_MANAGER,
      UserRole.COMPANY_CUSTOMS_USER,
    ],
    [UserRole.COMPANY_ROOT]: [
      UserRole.COMPANY_ADMIN,
      UserRole.COMPANY_STANDARD_USER,
      UserRole.COMPANY_ADVANCED_USER,
      UserRole.COMPANY_LOCATION_MANAGER,
    ],
    [UserRole.COMPANY_ADMIN]: [
      UserRole.COMPANY_ADMIN,
      UserRole.COMPANY_STANDARD_USER,
      UserRole.COMPANY_ADVANCED_USER,
      UserRole.COMPANY_LOCATION_MANAGER,
    ],
    // Only roles below Location Manager — not another Location Manager. Bounded to its held
    // locations by the use-case.
    [UserRole.COMPANY_LOCATION_MANAGER]: [UserRole.COMPANY_STANDARD_USER, UserRole.COMPANY_ADVANCED_USER],
  },
  [CompanyType.FLOORING]: {
    [UserRole.SUPER_ADMIN]: [
      UserRole.COMPANY_ROOT,
      UserRole.COMPANY_ADMIN,
      UserRole.FLOORING_DEALERSHIP_MANAGER,
      UserRole.FLOORING_FINANCE_MANAGER,
      UserRole.FLOORING_MANAGER,
      UserRole.FLOORING_AUDITOR,
    ],
    [UserRole.COMPANY_ROOT]: [
      UserRole.COMPANY_ADMIN,
      UserRole.FLOORING_DEALERSHIP_MANAGER,
      UserRole.FLOORING_FINANCE_MANAGER,
      UserRole.FLOORING_MANAGER,
      UserRole.FLOORING_AUDITOR,
    ],
    [UserRole.COMPANY_ADMIN]: [
      UserRole.COMPANY_ADMIN,
      UserRole.FLOORING_DEALERSHIP_MANAGER,
      UserRole.FLOORING_FINANCE_MANAGER,
      UserRole.FLOORING_MANAGER,
      UserRole.FLOORING_AUDITOR,
    ],
    [UserRole.FLOORING_AUDITOR]: [
      UserRole.FLOORING_DEALERSHIP_MANAGER,
      UserRole.FLOORING_FINANCE_MANAGER,
      UserRole.FLOORING_MANAGER,
    ],
  },
};

/** The roles `callerRole` may assign within `companyType`; empty when the caller may assign none. */
export function getAssignableRoles(companyType: CompanyType, callerRole: UserRole): UserRole[] {
  return determineRoleConfig[companyType]?.[callerRole] ?? [];
}
